import React, {useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import {PoppaysLoader} from '@lightricks/react-design-system';
import {JsonApiError} from '@/utils/jsonapiClient';
import {raiseFlashMessageError} from '@/utils/raiseFlashMessage';
import {ROUTES} from '@/config/routesConstants';
import {ProfileProps} from '@/types/creatorProfile';
import {DashboardUser} from '@/types/dashboardUser';
import CreatorProfile from '@/components/creator-profile';
import Page from '@/components/page';
import useCampaignQuery from '@/hooks/queries/use-campaign-query';
import useCreatorProfileQuery from '@/hooks/queries/use-creator-profile-query';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useBrandId from '@/hooks/use-brand-id';
import useNavigation from '@/hooks/use-navigation';
import useParams from '@/hooks/use-params';
import CampaignNetworkContext from '@/contexts/CampaignNetworkContext';
import styles from './Profile.module.scss';

function Profile({customLoader}: {customLoader?: React.ReactNode}) {
  const {creatorId} = useParams();
  const [searchParams] = useSearchParams();
  const {data: dashboardUser} = useDashboardUserQuery({});
  const navigation = useNavigation();

  const campaignId = searchParams.get('campaignId') || undefined;
  const {campaign} = useCampaignQuery({
    campaignId,
    enabled: !!campaignId,
  });
  const campaignNetwork = campaign?.network ?? undefined;

  const brandId = useBrandId();
  const {
    profile,
    error,
    isLoading,
    isError: isErrorProfile,
  } = useCreatorProfileQuery({
    profileId: creatorId,
    brandId,
  });

  useEffect(() => {
    if (error) {
      const jsonApiError = error as JsonApiError;
      switch (Number(jsonApiError.status)) {
        case 404: {
          navigation.navigate(ROUTES.NOT_FOUND);
          return;
        }
        default: {
          raiseFlashMessageError();
        }
      }
    }
  }, [error]);

  if (isLoading || !dashboardUser) {
    return customLoader || <PoppaysLoader />;
  }

  if (isErrorProfile) {
    return null;
  }

  return (
    <CampaignNetworkContext.Provider value={campaignNetwork}>
      <Page
        id="creator-profile"
        className={styles.profileContainer}
        childrenClassName={styles.profileWrapper}
      >
        <CreatorProfile
          testID="creator-profile"
          profile={profile as ProfileProps}
          dashboardUser={dashboardUser as DashboardUser}
          displayPublicDataOnly={false}
          componentAllowsAddMembership
          componentAllowsCollaboration
          componentAllowsInviteToCampaign
          canViewAudience
          canViewSocialHealth
          canViewPortfolio
          canViewPosts
          canViewProfileNotes
        />
      </Page>
    </CampaignNetworkContext.Provider>
  );
}

export default Profile;
